/**
 * Secondary footer "section rail" for the /scenarios detail view (mobile).
 *
 * Sits as a thin second layer directly above the main NativeTabBar and gives
 * quick scroll-to navigation across the (otherwise long, desktop-era) scenario
 * sections, with a scroll-spy active state. Chips auto-filter to the sections
 * actually present for the current scenario, and the active chip auto-scrolls
 * into view within the rail.
 *
 * Order = on-page scroll order (so the active highlight moves monotonically as
 * you scroll). "Overview" jumps to the top.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Info, Layers, Calendar, Building2, Sparkles, Globe, HandHeart, Receipt, Scale } from "lucide-react";
import { useBrand } from "@/config/brands";

interface RailItem {
  key: string;
  /** Anchor element id, or null to scroll to the top (Overview). */
  id: string | null;
  label: string;
  icon: React.ElementType;
}

// On-page (scroll) order.
const ITEMS: RailItem[] = [
  { key: "overview", id: null, label: "Overview", icon: Info },
  { key: "severity-variants", id: "severity-variants", label: "Variants", icon: Layers },
  { key: "cost-timeline", id: "cost-timeline", label: "Timeline", icon: Calendar },
  { key: "code-breakdown", id: "code-breakdown", label: "Code Breakdown", icon: Receipt },
  { key: "price-comparison", id: "price-comparison", label: "Price Comparison", icon: Scale },
  { key: "nearby-hospitals", id: "nearby-hospitals", label: "Hospitals", icon: Building2 },
  { key: "dignity-guide", id: "dignity-guide", label: "Dignity", icon: Sparkles },
  { key: "international", id: "international", label: "Global", icon: Globe },
  { key: "care-resources", id: "care-resources", label: "Care", icon: HandHeart },
];

export default function ScenarioSectionRail({
  dark,
  scenarioKey,
  modalKeys,
  onOpenSection,
  activeModal,
  bottomOffset,
}: {
  dark: boolean;
  scenarioKey: string | number;
  /** Section keys that open as a full-screen modal instead of scroll-jumping. */
  modalKeys?: Set<string>;
  onOpenSection?: (key: string) => void;
  /** Currently-open modal key, so its chip reads active. */
  activeModal?: string | null;
  /** CSS bottom offset so the rail rides above the active footer (native tab bar
   *  vs desktop/mobile AppFooter). Defaults to the native tab-bar position. */
  bottomOffset?: string;
}) {
  const isModal = (k: string) => !!modalKeys?.has(k);
  // Dignity is AHP's mascot; the sibling brands don't render her section, so
  // don't offer a chip that scrolls to nothing.
  const brand = useBrand();
  const items = useMemo(() => brand.key === 'ahp' ? ITEMS : ITEMS.filter((it) => it.key !== 'dignity-guide'), [brand.key]);
  const [present, setPresent] = useState<RailItem[]>([ITEMS[0]]);
  const [active, setActive] = useState<string>("overview");
  const railRef = useRef<HTMLDivElement>(null);

  // Which sections actually rendered for this scenario? Re-scan when it changes.
  // Modal-launched sections live in a modal (not inline), so they're always
  // considered present rather than checked against a DOM anchor.
  useEffect(() => {
    // Section anchors mount asynchronously (static-cache hydration), so a
    // single 350ms scan loses the race on direct URL loads and the rail
    // never appears. Retry with backoff until anchors show up (or give up
    // after ~12s for scenarios that genuinely have no inline sections).
    let raf = 0;
    let timer: ReturnType<typeof setTimeout>;
    let tries = 0;
    const scan = () => {
      const found = items.filter((it) => it.id === null || isModal(it.key) || document.getElementById(it.id));
      if (found.length > 1) {
        setPresent(found);
        return;
      }
      setPresent([ITEMS[0]]);
      if (++tries < 12) timer = setTimeout(() => { raf = requestAnimationFrame(scan); }, tries < 4 ? 350 : 1000);
    };
    timer = setTimeout(() => { raf = requestAnimationFrame(scan); }, 350);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [scenarioKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll-spy: the active section is the last one whose top has scrolled above
  // a trigger line just below the fixed header.
  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      const headerH = (document.querySelector("header") as HTMLElement)?.offsetHeight ?? 70;
      const threshold = headerH + 56;
      let current = "overview";
      for (const it of present) {
        if (!it.id) continue;
        const el = document.getElementById(it.id);
        if (el && el.getBoundingClientRect().top <= threshold) current = it.key;
      }
      setActive(current);
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();
    return () => window.removeEventListener("scroll", onScroll);
  }, [present]);

  // Keep the active chip visible in the rail.
  useEffect(() => {
    const el = railRef.current?.querySelector<HTMLElement>(`[data-rail-key="${active}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const go = useCallback((it: RailItem) => {
    if (isModal(it.key) && onOpenSection) {
      onOpenSection(it.key);
      return;
    }
    if (!it.id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(it.id);
    if (!el) return;
    const headerH = (document.querySelector("header") as HTMLElement)?.offsetHeight ?? 70;
    const y = el.getBoundingClientRect().top + window.scrollY - headerH - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, []);

  const barBg = dark ? "rgba(13,30,53,0.94)" : "rgba(255,255,255,0.94)";
  const border = dark ? "rgba(var(--civic-gold-rgb),0.22)" : "rgba(var(--civic-ink-rgb),0.10)";
  const idle = dark ? "rgba(var(--civic-warm-rgb),0.62)" : "rgba(var(--civic-ink-rgb),0.62)";
  const accent = dark ? "var(--civic-gold)" : "var(--civic-red)";
  const activeBg = dark ? "rgba(var(--civic-gold-rgb),0.16)" : "rgba(var(--civic-red-rgb),0.08)";

  if (present.length <= 1) return null;

  return (
    <div
      ref={railRef}
      data-rail-bar
      className="fixed left-0 right-0 flex items-stretch gap-1.5 px-2.5 overflow-x-auto no-scrollbar"
      style={{
        // Ride directly above the NativeTabBar: its content is ~59px and it owns
        // the bottom safe-area inset, so mirror both here (pure CSS — no fragile
        // measurement, and it can't land behind the taller-on-device tab bar).
        // Match the NativeTabBar's trimmed bottom inset so the rail rides
        // directly above it with no gap. bottomOffset overrides for web footers.
        bottom: bottomOffset ?? "calc(59px + max(env(safe-area-inset-bottom, 0px) - 26px, 6px))",
        zIndex: 9990,
        height: 46,
        backgroundColor: barBg,
        borderTop: `1px solid ${border}`,
        backdropFilter: "saturate(180%) blur(16px)",
        WebkitBackdropFilter: "saturate(180%) blur(16px)",
        scrollbarWidth: "none",
      }}
    >
      {present.map((it) => {
        const Icon = it.icon;
        const on = activeModal ? activeModal === it.key : active === it.key;
        return (
          <button
            key={it.key}
            data-rail-key={it.key}
            onClick={() => go(it)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 my-1.5 rounded-full transition-colors"
            style={{
              color: on ? accent : idle,
              backgroundColor: on ? activeBg : "transparent",
              border: `1px solid ${on ? `${accent}55` : "transparent"}`,
              fontFamily: "'Instrument Sans', system-ui, sans-serif",
              fontSize: 12,
              fontWeight: on ? 700 : 500,
              whiteSpace: "nowrap",
            }}
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={on ? 2.4 : 2} />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
